import React, { useState } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import {
  Navbar,
  Nav,
  Container,
  NavDropdown,
  Offcanvas,
} from "react-bootstrap";
import { useAuth } from "../contexts/AuthContext";
import {FaTachometerAlt, FaUsers, FaQuestionCircle, FaFileImport, FaCheckCircle, FaUser, FaUserFriends, FaBars, FaSignOutAlt,} from "react-icons/fa";

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isAdmin = user?.role === "admin";
  const isUserRoute = location.pathname.startsWith("/user");
  const isAdminRoute = location.pathname.startsWith("/admin");
  const userInitials =
    `${user?.profile?.firstName?.[0] || ""}${user?.profile?.lastName?.[0] || ""}` ||
    user?.username?.[0] ||
    "?";

  const closeMobileNav = () => setMobileNavOpen(false);

  const mobileNavLink = (to, label, Icon, active) => (
    <Nav.Link
      as={Link}
      to={to}
      onClick={closeMobileNav}
      className={`mobile-drawer-link${active ? " active" : ""}`}
      aria-current={active ? "page" : undefined}
      key={to}
    >
      <Icon aria-hidden="true" />
      <span>{label}</span>
    </Nav.Link>
  );

  return (
    <>
      <Navbar bg="white" expand="lg" className="app-navbar">
        <Container fluid>
          <Navbar.Brand
            as={Link}
            to={isAdmin ? "/admin" : "/user"}
            className="text-primary fw-bold"
          >
            AAP Gujarat Election
          </Navbar.Brand>

          <div className="mobile-navbar-actions d-lg-none">
            {isUserRoute ? (
              <Link
                to="/user/profile"
                className="mobile-navbar-avatar"
                aria-label="Open your profile"
                aria-current={
                  location.pathname === "/user/profile" ? "page" : undefined
                }
              >
                {userInitials}
              </Link>
            ) : (
              <span className="mobile-navbar-avatar" aria-hidden="true">
                {userInitials}
              </span>
            )}
            <button
              type="button"
              className="mobile-navbar-toggle"
              aria-label="Open navigation menu"
              aria-controls="mobile-navigation-drawer"
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen(true)}
            >
              <FaBars aria-hidden="true" />
            </button>
          </div>

          <Nav className="ms-auto d-none d-lg-flex">
            <NavDropdown
              className="fw-bold text-dark"
              title={<>{user?.username}</>}
              align="end"
            >
              <NavDropdown.Item onClick={handleLogout} className="text-sky-600">
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Container>
      </Navbar>

      <Offcanvas
        id="mobile-navigation-drawer"
        show={mobileNavOpen}
        onHide={closeMobileNav}
        placement="start"
        className="mobile-navigation-drawer"
        aria-labelledby="mobile-navigation-title"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title id="mobile-navigation-title">
            AAP Gujarat Election
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="mobile-drawer-body">
          <div className="mobile-drawer-user">
            <span className="mobile-drawer-avatar" aria-hidden="true">
              {userInitials}
            </span>
            <div className="mobile-drawer-user-copy">
              <strong>{user?.email || user?.username}</strong>
            </div>
          </div>

          <Nav className="mobile-drawer-nav">
            {isAdmin && isAdminRoute && (
              <>
                {mobileNavLink(
                  "/admin/dashboard",
                  "Dashboard",
                  FaTachometerAlt,
                  location.pathname === "/admin/dashboard",
                )}
                {mobileNavLink(
                  "/admin/users",
                  "User Management",
                  FaUsers,
                  location.pathname.startsWith("/admin/users"),
                )}
                {mobileNavLink(
                  "/admin/voters",
                  "All Voters",
                  FaUserFriends,
                  location.pathname === "/admin/voters",
                )}
                {mobileNavLink(
                  "/admin/questions",
                  "Questions",
                  FaQuestionCircle,
                  location.pathname === "/admin/questions",
                )}
                {mobileNavLink(
                  "/admin/import-data",
                  "Import Data",
                  FaFileImport,
                  location.pathname === "/admin/import-data",
                )}
                {mobileNavLink(
                  "/admin/answer-questions",
                  "Answer Questions",
                  FaCheckCircle,
                  location.pathname === "/admin/answer-questions",
                )}
              </>
            )}
            {!isAdmin && isUserRoute && (
              <>
                {mobileNavLink(
                  "/user/dashboard",
                  "Dashboard",
                  FaTachometerAlt,
                  location.pathname === "/user/dashboard",
                )}
                {mobileNavLink(
                  "/user/profile",
                  "Profile",
                  FaUser,
                  location.pathname === "/user/profile",
                )}
              </>
            )}
          </Nav>

          <div className="mobile-drawer-footer">
            <button
              type="button"
              className="mobile-drawer-logout"
              onClick={() => {
                closeMobileNav();
                handleLogout();
              }}
            >
              <FaSignOutAlt aria-hidden="true" />
              <span>Logout</span>
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <div className="d-flex align-items-stretch layout-shell">
        <div className="sidebar d-none d-lg-block flex-shrink-0 bg-white border-end">
          <div className="p-3">
            <h5 className="fw-bold mb-3 text-dark">
              {isAdmin ? "Admin Panel" : "User Panel"}
            </h5>

            <Nav className="flex-column gap-1">
              {isAdmin && isAdminRoute && (
                <>
                  <Nav.Link
                    as={Link}
                    to="/admin/dashboard"
                    className="sidebar-link"
                  >
                    <FaTachometerAlt /> <span>Dashboard</span>
                  </Nav.Link>

                  <Nav.Link
                    as={Link}
                    to="/admin/users"
                    className="sidebar-link"
                  >
                    <FaUsers /> <span>User Management</span>
                  </Nav.Link>

                  <Nav.Link
                    as={Link}
                    to="/admin/voters"
                    className="sidebar-link"
                  >
                    <FaUserFriends /> <span>All Voters</span>
                  </Nav.Link>

                  <Nav.Link
                    as={Link}
                    to="/admin/questions"
                    className="sidebar-link"
                  >
                    <FaQuestionCircle /> <span>Questions</span>
                  </Nav.Link>

                  <Nav.Link
                    as={Link}
                    to="/admin/import-data"
                    className="sidebar-link"
                  >
                    <FaFileImport /> <span>Import Data</span>
                  </Nav.Link>

                  <Nav.Link
                    as={Link}
                    to="/admin/answer-questions"
                    className="sidebar-link"
                  >
                    <FaCheckCircle /> <span>Answer Questions</span>
                  </Nav.Link>
                </>
              )}
              {!isAdmin && isUserRoute && (
                <>
                  <Nav.Link
                    as={Link}
                    to="/user/dashboard"
                    className="sidebar-link"
                  >
                    <FaTachometerAlt /> <span>Dashboard</span>
                  </Nav.Link>

                  <Nav.Link
                    as={Link}
                    to="/user/profile"
                    className="sidebar-link"
                  >
                    <FaUser /> <span>Profile</span>
                  </Nav.Link>
                </>
              )}
            </Nav>
          </div>
        </div>
        <div className="flex-grow-1 layout-main">
          <Outlet />
        </div>
      </div>
    </>
  );
};

export default Layout;
